import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbTimepicker,
  NgbTimeStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { DatePositions, DateService, IDateFilter } from '@portal/shared/helpers';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators';
import { RegexPatterns } from '@portal/shared/ui/form/src';
import { ComparableDateService } from '@portal/shared/ui/comparable-date-range/src/lib/services/date.service';
import { ISelectInput } from '@portal/shared/ui/filter/src';
import { DateFilterQuickPick } from '@apps/portal/src/app/shared/components/filter/date/enums/quick-pick.enum';
import { QuickPicks } from '@portal/shared/ui/comparable-date-range/src/lib/enums/date.enum';
import { ToastService } from '@portal/shared/ui/toast/src';

declare const $localize;

@Component({
  selector: 'portal-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  standalone: false,
})
export class DatePickerComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() toDate: NgbDate;
  @Input() fromDate: NgbDate;
  @Input() toTime: NgbTimeStruct;
  @Input() fromTime: NgbTimeStruct;
  @Input() markDisabled: (date: NgbDate) => boolean;
  @Input() futureMaxDate: NgbDate;
  @Input() isCompareTo = false;
  @Input() dateRangeSelection: IDateFilter;
  @Input() showQuickPicks = false;
  @Input() showTimePicker = false;
  @Input() quickPicksList: ISelectInput[];
  @Input() disableTimePicker = false;
  @Input() quickPick: DateFilterQuickPick;
  @Input() oneHourTimeDifference = false;
  @Output() fromDateApplied: EventEmitter<NgbDate> = new EventEmitter();
  @Output() toDateApplied: EventEmitter<NgbDate> = new EventEmitter();
  @Output() fromTimeApplied: EventEmitter<NgbTimeStruct> = new EventEmitter();
  @Output() toTimeApplied: EventEmitter<NgbTimeStruct> = new EventEmitter();
  @Output() datePickerErrors: EventEmitter<string[]> = new EventEmitter();
  @Output() isInvalidDate?: EventEmitter<boolean> = new EventEmitter();
  @Output() isInvalidTime?: EventEmitter<boolean> = new EventEmitter();
  @Output() quickPickSelected: EventEmitter<DateFilterQuickPick> = new EventEmitter();
  @ViewChild('fromTimePicker', { static: false }) fromTimePicker!: NgbTimepicker;
  @ViewChild('toTimePicker', { static: false }) toTimePicker!: NgbTimepicker;
  @ViewChild('quickPickDropdown', { static: false }) quickPickDropdown!: ElementRef;
  dateFormat = this.dateService.getPlaceholder();
  hoveredDate: NgbDate;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  _fromDate: NgbDate;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _dateToShow: NgbDate;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _toDate: NgbDate;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _fromTime: NgbTimeStruct = { hour: 0, minute: 0, second: 0 };
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _toTime: NgbTimeStruct = { hour: 23, minute: 59, second: 59 };

  maxDate: NgbDate;
  pendingFromDateString = '';
  pendingToDateString = '';

  fromDateValue$ = new Subject<string>();
  toDateValue$ = new Subject<string>();

  fromDateErrorMessages: string[] = [];
  toDateErrorMessages: string[] = [];
  localFormatDate: DatePositions;
  isSmall = false;
  isMedium = false;
  isLarge = false;
  isQuickPickOpen = false;
  selectedQuickPick: DateFilterQuickPick;
  quickPickButtonText: string;
  invalidTime = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private dateService: DateService,
    private comparableDateService: ComparableDateService,
    private toastService: ToastService,
    private elementRef: ElementRef,
    public calendar: NgbCalendar,
    public formatter: NgbDateParserFormatter,
    private cd: ChangeDetectorRef,
  ) {
    this.maxDate = calendar.getToday();
    this.setScreenSizeParam();
  }
  ngAfterViewInit(): void {
    const timepickers = this.elementRef.nativeElement.querySelectorAll('button[tabindex="-1"]');
    timepickers.forEach((button: HTMLButtonElement) => {
      const isTimepickerButton =
        button.classList.contains('btn') &&
        button.classList.contains('btn-link') &&
        button.querySelector('.ngb-tp-chevron');

      if (isTimepickerButton) {
        button.removeAttribute('tabindex');
      }
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.setScreenSizeParam();
  }

  @HostListener('document:mousedown', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (
      this.isQuickPickOpen &&
      this.quickPickDropdown &&
      !this.quickPickDropdown.nativeElement.contains(event.target)
    ) {
      this.closeDropDown();
    }
  }

  ngOnInit(): void {
    // Set fromDate from input if given
    // Otherwise use default one, which is today-10days
    if (this.fromDate) {
      this._fromDate = this.fromDate;
      this.pendingFromDateString = this.formatter.format(this.fromDate);
    } else {
      this._fromDate = this.calendar.getPrev(this.calendar.getToday(), 'd', 10);
    }

    // Set toDate from input if given
    // Otherwise use default one, which is today
    if (this.toDate) {
      this._toDate = this.toDate;
      this._dateToShow = this.toDate;
      this.pendingToDateString = this.formatter.format(this.toDate);
    }

    // Sets navigation to current month
    if (!this._dateToShow) {
      this._dateToShow = this.calendar.getToday();
    }

    if (this.fromTime) {
      this._fromTime = this.fromTime;
    }

    if (this.toTime) {
      this._toTime = this.toTime;
    }

    setTimeout(() => {
      this.fromDateApplied.emit(this._fromDate);
      this.toDateApplied.emit(this._toDate);
      this.fromTimeApplied.emit(this._fromTime);
      this.toTimeApplied.emit(this._toTime);
      this.fromDateValue$.next(this.pendingFromDateString);
      this.toDateValue$.next(this.pendingToDateString);
    }, 0);

    const toParsedDate$ = (src$: Subject<string>): Observable<NgbDate> => this.dateChange(src$);

    const fromDate$ = toParsedDate$(this.fromDateValue$);
    const toDate$ = toParsedDate$(this.toDateValue$);

    combineLatest([fromDate$, toDate$])
      .pipe(
        filter(([fromDate, toDate]) => !!fromDate && !!toDate), // proceed only when BOTH are valid
        takeUntil(this.destroy$),
      )
      .subscribe(([fromDate, toDate]) => {
        this._fromDate = fromDate;
        this._toDate = toDate;
        this._dateToShow = fromDate;

        const isValid = this.validateInput();

        if (isValid) {
          this.fromDateApplied.emit(this._fromDate);
          this.toDateApplied.emit(this._toDate);
        }
        /* Force calendar repaint so range classes update */
        this.cd.detectChanges();
      });

    this.localFormatDate = this.dateService.getLocalFormatElements();
    if (this.quickPick) {
      this.setQuickPickButtonText(this.quickPick);
      this.selectedQuickPick = this.quickPick;
    } else {
      this.setCustomQuickPick();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.futureMaxDate) {
      this.maxDate = this.futureMaxDate || this.calendar.getToday();
    }
    if (changes.fromDate) {
      const newFromDate = changes.fromDate.currentValue;

      if (!this.areDatesEqual(newFromDate, this._fromDate)) {
        this._fromDate = newFromDate;

        const newPending = newFromDate ? this.formatter.format(newFromDate) : '';
        if (this.pendingFromDateString !== newPending) {
          this.pendingFromDateString = newPending;
        }
      }
    }

    if (changes.toDate) {
      const newToDate = changes.toDate.currentValue;

      if (!this.areDatesEqual(newToDate, this._toDate)) {
        this._toDate = newToDate;

        const newPending = newToDate ? this.formatter.format(newToDate) : '';
        if (this.pendingToDateString !== newPending) {
          this.pendingToDateString = newPending;
        }
      }
    }

    if (changes.fromTime) {
      const newFromTime = changes.fromTime.currentValue;
      if (
        newFromTime &&
        (this._fromTime?.hour !== newFromTime.hour ||
          this._fromTime?.minute !== newFromTime.minute ||
          this._fromTime?.second !== newFromTime.second)
      ) {
        this._fromTime = newFromTime;
        this.updateTimeView(this.fromTimePicker, this._fromTime);
      }
    }

    if (changes.toTime) {
      const newToTime = changes.toTime.currentValue;
      if (
        newToTime &&
        (this._toTime?.hour !== newToTime.hour ||
          this._toTime?.minute !== newToTime.minute ||
          this._toTime?.second !== newToTime.second)
      ) {
        this._toTime = newToTime;
        this.updateTimeView(this.toTimePicker, this._toTime);
      }
    }

    this._dateToShow = this._fromDate || this.calendar.getToday();

    this.validateInput();
    this.isInvalidDate.emit(
      this.fromDateErrorMessages.length > 0 || this.toDateErrorMessages.length > 0,
    );
  }

  areDatesEqual(date1: NgbDate, date2: NgbDate): boolean {
    return date1?.equals(date2);
  }

  fromDateChange(value: Observable<string>): Observable<NgbDate> {
    return this.dateChange(value);
  }

  toDateChange(value: Observable<string>): Observable<NgbDate> {
    return this.dateChange(value);
  }

  dateChange(value: Observable<string>): Observable<NgbDate> {
    return value.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((newValue: string) => {
        return this.getUpdatedValue(newValue);
      }),
    );
  }

  getUpdatedValue(newValue: string): Observable<NgbDate | null> {
    const parts = this.dateService.splitDate(newValue);
    const year = parts[this.localFormatDate.yearPost];

    // Reject if year isn't 4 digits
    if (!year || year.length !== 4) return of(null);

    return of(
      NgbDate.from({
        day: +parts[this.localFormatDate.dayPost],
        month: +parts[this.localFormatDate.monthPost],
        year: +year,
      }),
    );
  }

  onDateSelection(date: NgbDate): void {
    this.setCustomQuickPick();
    if (!this._toDate) {
      if (date.before(this._fromDate)) {
        this._fromDate = date;
        this.pendingFromDateString = this.formatter.format(this._fromDate);
        this.validateInput();
        this.fromDateApplied.emit(this._fromDate);
        return;
      }
      if (date.after(this._fromDate)) {
        this._toDate = date;
        this.pendingToDateString = this.formatter.format(this._toDate);
        this.validateInput();
        this.toDateApplied.emit(this._toDate);
        return;
      }
    }

    if (this._fromDate && !this._toDate && date.equals(this._fromDate)) {
      this._toDate = date;
      this.pendingToDateString = this.formatter.format(this._toDate);
      this.validateInput();
      this.toDateApplied.emit(this._toDate);
      this.validateTime();
      return;
    }

    this._fromDate = date;
    this.pendingFromDateString = this.formatter.format(this._fromDate);
    this._toDate = null;
    this.pendingToDateString = '';
    this.validateInput();
    this.fromDateApplied.emit(this._fromDate);
    this.toDateApplied.emit(null);
    return;
  }

  isHovered(date: NgbDate): boolean {
    return (
      this._fromDate &&
      !this._toDate &&
      this.hoveredDate &&
      date.after(this._fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate): boolean {
    return date.after(this._fromDate) && date.before(this._toDate);
  }

  isFirst(date: NgbDate): boolean {
    return date.equals(this._fromDate);
  }

  isLast(date: NgbDate): boolean {
    return this._toDate
      ? date.equals(this._toDate)
      : this.hoveredDate?.after(this._fromDate) && date.equals(this.hoveredDate);
  }

  isRange(date: NgbDate): boolean {
    return (
      date.equals(this._fromDate) ||
      date.equals(this._toDate) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  validateTime(): void {
    if (this._toDate.equals(this._fromDate)) {
      const fromDateTime = new Date();
      const toDateTime = new Date();

      fromDateTime.setHours(this._fromTime.hour);
      fromDateTime.setMinutes(this._fromTime.minute);
      fromDateTime.setSeconds(this._fromTime.second);

      toDateTime.setHours(this._toTime.hour);
      toDateTime.setMinutes(this._toTime.minute);
      toDateTime.setSeconds(this._toTime.second);

      if (fromDateTime.getTime() > toDateTime.getTime()) {
        this.fromTimeChange({ hour: 0, minute: 0, second: 0 });
        this.toTimeChange({ hour: 23, minute: 59, second: 59 });
      }
    }
  }

  validateInput(): boolean {
    this.fromDateErrorMessages = [];
    this.toDateErrorMessages = [];

    // --- FROM DATE Validation ---
    if (!this.pendingFromDateString || !this.pendingFromDateString.trim()) {
      this.fromDateErrorMessages.push($localize`Date is required`);
    } else if (this.isFromDateInvalid()) {
      this.fromDateErrorMessages.push($localize`Date is invalid`);
    } else if (this._fromDate && this._fromDate.after(this.maxDate)) {
      this.fromDateErrorMessages.push($localize`Date is greater than maximum date`);
    }

    // --- TO DATE Validation ---
    if (!this.pendingToDateString || !this.pendingToDateString.trim()) {
      this.toDateErrorMessages.push($localize`Date is required`);
    } else if (this.isToDateInvalid()) {
      this.toDateErrorMessages.push($localize`Date is invalid`);
    } else if (this._toDate && this._toDate.after(this.maxDate)) {
      this.toDateErrorMessages.push($localize`Date is greater than maximum date`);
    }

    // --- Cross-Field & Range Validation ---
    if (
      this._fromDate &&
      this._toDate &&
      this._fromDate.after(this._toDate) &&
      !this.fromDateErrorMessages.length &&
      !this.toDateErrorMessages.length
    ) {
      this.fromDateErrorMessages.push($localize`Start date is greater than end date`);
    }
    // --- Compare-To, Time, and Custom Validations ---
    if (this.isCompareTo) {
      if (this.toDateErrorMessages.length === 0 && !this.isValidDiffLength()) {
        this.fromDateErrorMessages.push(
          $localize`The date range must be the same number of days for comparison`,
        );
      }
      this.invalidTime = !this.hasSameTimeDifference(
        {
          start: this.convertUtcToLocalNgbTimeStruct(this.dateRangeSelection?.start),
          end: this.convertUtcToLocalNgbTimeStruct(this.dateRangeSelection?.end),
        },
        { start: this._fromTime, end: this._toTime },
      );
      this.isInvalidTime.emit(this.invalidTime);
      if (this.toDateErrorMessages.length === 0 && this.dateRangeSelection) {
        if (
          this.checkDateStringIsBeforeOrTheSame(
            this.dateRangeSelection.end,
            this.dateToCustomString(this._fromDate, this._fromTime),
          )
        ) {
          this.fromDateErrorMessages.push(
            $localize`This date range to compare must be earlier than the selected date range`,
          );
        }
      }
    }
    this.datePickerErrors.emit([...this.fromDateErrorMessages, ...this.toDateErrorMessages]);
    return this.fromDateErrorMessages.length === 0 && this.toDateErrorMessages.length === 0;
  }

  onFromDateChange(event): void {
    this.setCustomQuickPick();
    this.pendingFromDateString = event.target.value;
    if (!this.pendingFromDateString || this.pendingFromDateString.trim() === '') {
      this.fromDate = null;
      this._fromDate = null;
    }
    this.fromDateValue$.next(this.pendingFromDateString);
    this.validateInput();
  }

  onToDateChange(event): void {
    this.setCustomQuickPick();
    this.pendingToDateString = event.target.value;
    if (!this.pendingToDateString || this.pendingToDateString.trim() === '') {
      this.toDate = null;
      this._toDate = null;
    }
    this.toDateValue$.next(this.pendingToDateString);
    this.validateInput();
  }

  get fromDateValue(): string {
    if (this.pendingFromDateString !== '') return this.pendingFromDateString;
    if (this._fromDate) return this.formatter.format(this._fromDate);
    return '';
  }

  get toDateValue(): string {
    if (this.pendingToDateString !== '') return this.pendingToDateString;
    if (this._toDate) return this.formatter.format(this._toDate);
    return '';
  }

  dateToString(date: NgbDate): string {
    if (date && date.month && date.day && date.year) {
      return date.month + '/' + date.day + '/' + date.year;
    } else {
      return '';
    }
  }
  dateToCustomString(
    date: NgbDate,
    time: NgbTimeStruct = {
      hour: 22,
      minute: 0,
      second: 0,
    },
  ): string {
    if (date && date.month && date.day && date.year) {
      // Construct a local Date object using the NgbDate and NgbTime
      const localDate = new Date(
        date.year,
        date.month - 1,
        date.day,
        time.hour,
        time.minute,
        time.second || 0,
      );

      // Convert the local date to UTC by using the `toISOString()` method
      const utcString = localDate.toISOString();

      return utcString; // It will return in the format "YYYY-MM-DDTHH:mm:ss.sssZ"
    } else {
      return ''; // Return empty string if date is invalid
    }
  }

  checkDateStringIsBeforeOrTheSame(start: string, end: string): boolean {
    if (start && end) {
      return new Date(end).getTime() - new Date(start).getTime() >= 0;
    }
    return true;
  }

  isFromDateInvalid(): boolean {
    return (
      (this.pendingFromDateString && !RegexPatterns.date.test(this.pendingFromDateString)) ||
      (this._fromDate && !this.calendar.isValid(this._fromDate))
    );
  }

  isToDateInvalid(): boolean {
    return (
      (this.pendingToDateString && !RegexPatterns.date.test(this.pendingToDateString)) ||
      (this._toDate && !this.calendar.isValid(this._toDate))
    );
  }

  isValidDiffLength(): boolean {
    if (
      this._fromDate &&
      this._toDate &&
      this.dateRangeSelection &&
      this.dateRangeSelection?.start &&
      this.dateRangeSelection?.end
    ) {
      const compareDate: IDateFilter = {
        start: this.dateToCustomString(this._fromDate),
        end: this.dateToCustomString(this._toDate),
      };
      return (
        this.comparableDateService.getDifferenceInDays(this.dateRangeSelection) ===
        this.comparableDateService.getDifferenceInDays(compareDate)
      );
    }
    return true;
  }

  setScreenSizeParam(): void {
    this.isSmall = window.innerWidth < 350;
    this.isMedium = !this.isSmall && window.innerWidth < 768;
    this.isLarge = window.innerWidth > 767;
  }

  onQuickPickSelect(quickPick: ISelectInput): void {
    if (quickPick) {
      const quickPickId = quickPick.id as DateFilterQuickPick;
      this.quickPickButtonText = quickPick.text as DateFilterQuickPick;
      this.selectedQuickPick = quickPickId;
      this.quickPickSelected.emit(quickPickId);
      this.closeDropDown();
    }
  }

  toggleDropDown($event): void {
    $event.preventDefault();
    this.isQuickPickOpen = !this.isQuickPickOpen;
  }

  closeDropDown(): void {
    this.isQuickPickOpen = false;
  }

  fromTimeChange(time: NgbTimeStruct): void {
    if (this._toDate.equals(this._fromDate)) {
      const fromDateTime = new Date();
      const toDateTime = new Date();

      // Set time for fromDateTime
      fromDateTime.setHours(time.hour);
      fromDateTime.setMinutes(time.minute);
      fromDateTime.setSeconds(time.second || 0);

      // Set time for toDateTime
      toDateTime.setHours(this.oneHourTimeDifference ? this._toTime.hour - 1 : this._toTime.hour);
      toDateTime.setMinutes(this._toTime.minute);
      toDateTime.setSeconds(this._toTime.second || 0);

      // Check if fromTime exceeds toTime
      if (fromDateTime.getTime() > toDateTime.getTime()) {
        this._fromTime = {
          ...this._toTime,
          hour: this.oneHourTimeDifference ? this._toTime.hour - 1 : this._toTime.hour,
          second: 0,
        }; // Adjust to match toTime
        if (this.oneHourTimeDifference) {
          this.toastService.showToast($localize`Please note duration should be minimum one hour.`);
        }
      } else {
        this._fromTime = time; // Accept the new time
      }
    } else {
      this._fromTime = time;
    }
    this.updateTimeView(this.fromTimePicker, this._fromTime);
    this.fromTimeApplied.emit(this._fromTime);
  }

  toTimeChange(time: NgbTimeStruct): void {
    if (this._toDate.equals(this._fromDate)) {
      const fromDateTime = new Date();
      const toDateTime = new Date();

      // Set time for fromDateTime
      fromDateTime.setHours(
        this.oneHourTimeDifference ? this._fromTime.hour + 1 : this._fromTime.hour,
      );
      fromDateTime.setMinutes(this._fromTime.minute);
      fromDateTime.setSeconds(this._fromTime.second || 0);

      // Set time for toDateTime
      toDateTime.setHours(time.hour);
      toDateTime.setMinutes(time.minute);
      toDateTime.setSeconds(time.second || 0);

      // Check if toTime is lower than fromTime
      if (toDateTime.getTime() < fromDateTime.getTime()) {
        this._toTime = {
          ...this._fromTime,
          hour: this.oneHourTimeDifference ? this._fromTime.hour + 1 : this._fromTime.hour,
          second: 59,
        }; // Adjust to match fromTime
        if (this.oneHourTimeDifference) {
          this.toastService.showToast($localize`Please note duration should be minimum one hour.`);
        }
      } else {
        this._toTime = time; // Accept the new time
      }
    } else {
      this._toTime = time;
    }
    this.updateTimeView(this.toTimePicker, this._toTime);
    this.toTimeApplied.emit(this._toTime);
  }

  setQuickPickButtonText(quickPickId: string): void {
    this.quickPickButtonText = this.quickPicksList?.find((item) => item.id === quickPickId)?.text;
  }

  setCustomQuickPick(): void {
    if (this.quickPicksList?.find((item) => item.id === QuickPicks.Custom)) {
      this.setQuickPickButtonText(QuickPicks.Custom);
      this.selectedQuickPick = DateFilterQuickPick.Custom;
    } else {
      this.setQuickPickButtonText('');
      this.selectedQuickPick = null;
    }
  }

  selectText(event: FocusEvent): void {
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT') {
      const input = target as HTMLInputElement;
      input.select();
    }
  }

  /**
   * Temporary fix for NgbTimepicker UI not updating correctly when entering the same invalid value twice.
   * This forces a UI refresh by clearing and reapplying the timepicker value.
   *
   * Once this issue is fixed in the ng-bootstrap library, this method should be removed.
   *
   * Related Issue: https://github.com/ng-bootstrap/ng-bootstrap/issues/4514
   */
  updateTimeView(timePickerToUpdate: NgbTimepicker, newValue: NgbTimeStruct): void {
    if (!newValue) return;
    timePickerToUpdate.writeValue(null); // Clear the current value
    this.cd.detectChanges();
    timePickerToUpdate.writeValue(newValue); // Reapply the corrected value
  }

  private areRangesOnTheSameDay(
    range1: { start: string; end: string },
    range2: { start: string; end: string },
  ): boolean {
    if (range1 && range2) {
      const range1StartDate = new Date(range1.start);
      const range1EndDate = new Date(range1.end);
      const range2StartDate = new Date(range2.start);
      const range2EndDate = new Date(range2.end);

      const isSameDayRange1 = this.isSameDay(range1StartDate, range1EndDate);
      const isSameDayRange2 = this.isSameDay(range2StartDate, range2EndDate);

      return isSameDayRange1 && isSameDayRange2 && this.isSameDay(range1StartDate, range2StartDate);
    } else {
      return false;
    }
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  private hasSameTimeDifference(
    range1: { start: NgbTimeStruct; end: NgbTimeStruct },
    range2: { start: NgbTimeStruct; end: NgbTimeStruct },
  ): boolean {
    if (range1 && range2) {
      const range1StartSeconds = this.timeToSeconds(range1.start);
      const range1EndSeconds = this.timeToSeconds(range1.end);
      const range2StartSeconds = this.timeToSeconds(range2.start);
      const range2EndSeconds = this.timeToSeconds(range2.end);

      const range1Diff = range1EndSeconds - range1StartSeconds;
      const range2Diff = range2EndSeconds - range2StartSeconds;

      return range1Diff === range2Diff;
    } else {
      return false;
    }
  }

  private timeToSeconds(time: NgbTimeStruct): number {
    return time.hour * 3600 + time.minute * 60 + time.second;
  }

  private convertUtcToLocalNgbTimeStruct(utcDateString: string): NgbTimeStruct {
    const date = new Date(utcDateString);

    const hour = date.getHours();
    const minute = date.getMinutes();
    const second = date.getSeconds();

    return { hour, minute, second };
  }
}
